import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ROLES, DayListEntryType } from '../../resources-list.component';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { TranslocoService } from '@ngneat/transloco';

const COLORS = ['#b90060', '#ef6096', '#666666', '#aaaaaa'];

@Component({
  selector: 'app-resources-chart',
  templateUrl: './resources-chart.component.html',
  styleUrls: ['./resources-chart.component.scss']
})
export class ResourcesChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() data: Array<DayListEntryType> = [];
  @ViewChild('chartContainer') chartContainer?: ElementRef = null;

  private chart?: am4charts.XYChart;

  constructor(
    private zone: NgZone,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.buildChartData();
    }
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.chart?.dispose();
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chartContainer === null) {
        return;
      }

      am4core.useTheme(am4themes_animated);

      let chart = am4core.create(this.chartContainer.nativeElement, am4charts.XYChart);
      chart.height = 300;

      // x axis
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'date';
      categoryAxis.title.text = this.transloco.translate('common.labels.day');
      categoryAxis.renderer.labels.template.adapter.add("text", function(text, target) {
        if (text === '{category}' && target.dataItem?.properties?.category) {
          return target.dataItem?.properties?.category.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3.$2');
        }
        return text;
      });

      // y axis
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = this.transloco.translate('common.labels.amount');

      // series for all roles
      ROLES.forEach((role: string, index: number) => {
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = role;
        series.dataFields.categoryX = 'date';
        series.name = this.getRoleLabel(role);
        series.stacked = true;
        const color = am4core.color(COLORS[index % COLORS.length]);
        series.columns.template.fill = color;
        series.columns.template.stroke = color;
        series.tooltipText = '{name}: {valueY}';
        series.tooltip.getFillFromObject = true;
        series.columns.template.width = am4core.percent(70);
      });

      // tooltip settings
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;

      // legend
      chart.legend = new am4charts.Legend();
      chart.legend.width = am4core.percent(100);
      chart.legend.marginTop = 10;
      chart.legend.labels.template.minWidth = 150;

      this.chart = chart;
      this.buildChartData();

    });
  }

  private buildChartData(): void {
    if (this.chart) {
      const result = [];
      this.data.forEach((entry: DayListEntryType) => {
        let dataPoint = result.find(x => x.date === entry.date) || null;
        if (dataPoint === null) {
          dataPoint = { date: entry.date };
          result.push(dataPoint);
        }
        ROLES.forEach((role: string) => {
          dataPoint[role] = entry[role];
        });
      });
      this.chart.data = result;
    }
  }

  private getRoleLabel(role: string): string {
    return this.transloco.translate(`common.labels.${role}`);
  }

}
